<template>
  <div>
    <div class="card mb-2">
      <div class="card-header px-2 py-2 d-flex justify-content-between align-items-center">
        <h5 class="d-flex justify-content-between align-items-center font-weight-bold text-muted m-0">
          <button
            class="btn btn-link text-muted font-weight-bold p-0 header-btn"
            @click="ui.show = !ui.show"
          >
            Attributes
          </button>
          <button
            type="button"
            class="btn btn-link btn-sm"
            @click="removeAllRules"
            :disabled="$props.disabled || $props.value.length === 0"
          >
            Delete Rules
          </button>
        </h5>
      </div>
      <div
        v-show="ui.show"
        class="card-body px-2 py-2"
      >
        <div class="form-row">
          <div
            v-if="error.show"
            v-html="error.msg"
            :class="`col-12 mb-2 alert alert-${error.type} d-block py-2`"
          />
          <div class="col-2 mb-2">
            <label class="text-truncate d-block small mb-0">Property Type</label>
            <select
              :class="{
                'form-control': true,
                'rule-none': inputClasses['PropertyType'].ruleNone,
                'rule-add': inputClasses['PropertyType'].ruleAdd
              }"
              name="PropertyType"
              @input="updatePropertyType"
              :disabled="$props.disabled"
            >
              <template v-for="type in propertyTypes">
                <option
                  :key="type.Id"
                  :value="`${type.Id}:${type.Group}`"
                  :selected="values['PropertyType'] === `${type.Id}:${type.Group}`"
                >
                  {{ type.Labels[ 'en' ] }}
                </option>
              </template>
            </select>
            <button
              type="button"
              class="d-block btn btn-link btn-sm small p-0 m-0"
              @click="removeRule('PropertyType')"
              style="font-size:10px;"
            >
              Reset
            </button>
          </div>
          <div class="col-2 mb-2">
            <label class="text-truncate d-block small mb-0">Offer Type</label>
            <select
              :class="{
                'form-control': true,
                'rule-none': inputClasses['OfferType'].ruleNone,
                'rule-add': inputClasses['OfferType'].ruleAdd
              }"
              name="OfferType"
              @input="updateOfferType"
              :disabled="$props.disabled"
            >
              <template v-for="type in listingTypes">
                <option
                  :key="type.Name"
                  :value="type.Name"
                  :selected="values['OfferType'] === type.Name"
                >
                  {{ type.Labels[ 'en' ] }}
                </option>
              </template>
            </select>
            <button
              type="button"
              class="d-block btn btn-link btn-sm small p-0 m-0"
              style="font-size:10px;"
              @click="removeRule('OfferType')"
            >
              Reset
            </button>
          </div>

          <template v-if="values.OfferType=== FOR_SALE || values.OfferType === FOR_RENT">
            <div class="col-2 mb-3">
              <label class="text-truncate d-block small mb-0">Price (&euro;)</label>
              <div class="position-relative">
                <input
                  type="number"
                  :class="{'form-control': true, 'rule-none': inputClasses['PriceInEuro'].ruleNone, 'rule-add': inputClasses['PriceInEuro'].ruleAdd}"
                  :value="values.PriceInEuro"
                  name="PriceInEuro"
                  @input="update"
                  :disabled="$props.disabled"
                >
                <button
                  type="button"
                  class="btn btn-xs p-0 m-0 btn-rule-reset"
                  title="Remove Rule"
                  v-if="inputClasses['PriceInEuro'].ruleAdd"
                  @click="removeRule('PriceInEuro')"
                >
                  <i class="jam jam-close-circle-f" />
                </button>
              </div>
            </div>
          </template>
          <template v-else-if="values.OfferType === FOR_HOLIDAY">
            <div class="col-2 mb-3">
              <label class="text-truncate d-block small mb-0">Price Min</label>
              <div class="position-relative">
                <input
                  type="number"
                  :class="{'form-control': true, 'rule-none': inputClasses['PriceInEuro'].ruleNone, 'rule-add': inputClasses['PriceInEuro'].ruleAdd}"
                  :value="values.PriceInEuro"
                  name="PriceInEuro"
                  @input="update"
                  :disabled="$props.disabled"
                >
                <button
                  type="button"
                  class="btn btn-xs p-0 m-0 btn-rule-reset"
                  title="Remove Rule"
                  v-if="inputClasses['PriceInEuro'].ruleAdd"
                  @click="removeRule('PriceInEuro')"
                >
                  <i class="jam jam-close-circle-f" />
                </button>
              </div>
            </div>
            <div class="col-2 mb-3">
              <label class="text-truncate d-block small mb-0">Price Max</label>
              <div class="position-relative">
                <input
                  type="number"
                  :class="{'form-control': true, 'rule-none': inputClasses['HighestPriceInReferedCurrency'].ruleNone, 'rule-add': inputClasses['HighestPriceInReferedCurrency'].ruleAdd}"
                  name="HighestPriceInReferedCurrency"
                  @input="update"
                  :value="values.HighestPriceInReferedCurrency"
                  :disabled="$props.disabled"
                >
                <button
                  type="button"
                  class="btn btn-xs p-0 m-0 btn-rule-reset"
                  title="Remove Rule"
                  v-if="inputClasses['HighestPriceInReferedCurrency'].ruleAdd"
                  @click="removeRule('HighestPriceInReferedCurrency')"
                  :disabled="$props.disabled"
                >
                  <i class="jam jam-close-circle-f" />
                </button>
              </div>
            </div>
          </template>

          <template v-for="feature in corePropertyFeatures">
            <div
              class="col-2 mb-2"
              :key="feature.name"
            >
              <label
                class="text-truncate d-block small mb-0"
                :for="feature.name"
                v-html="feature.label"
              />
              <div class="position-relative">
                <template v-if="feature.type === 'text'">
                  <input
                    :id="feature.name"
                    :class="{
                      'form-control': true,
                      'rule-none': inputClasses[feature.mapping].ruleNone,
                      'rule-add': inputClasses[feature.mapping].ruleAdd,
                      'has-error': inputClasses[feature.mapping].hasError
                    }"
                    :name="feature.mapping"
                    placeholder="No Value"
                    type="text"
                    :value="values[feature.mapping]"
                    @input.passive="update"
                    :disabled="$props.disabled"
                    :title="feature.title || ''"
                  >
                </template>
                <template v-else-if="feature.type === 'number'">
                  <input
                    :id="feature.name"
                    :class="{
                      'form-control': true,
                      'rule-none': inputClasses[feature.mapping].ruleNone,
                      'rule-add': inputClasses[feature.mapping].ruleAdd,
                      'has-error': inputClasses[feature.mapping].hasError
                    }"
                    :name="feature.mapping"
                    placeholder="No Value"
                    type="number"
                    :value="values[feature.mapping]"
                    @input.passive="update"
                    :disabled="$props.disabled"
                    :title="feature.title || ''"
                  >
                </template>
                <template v-else-if="feature.type === 'select'">
                  <select
                    :class="{
                      'form-control': true,
                      'rule-none': inputClasses[feature.mapping].ruleNone,
                      'rule-add': inputClasses[feature.mapping].ruleAdd,
                      'has-error': inputClasses[feature.mapping].hasError
                    }"
                    :name="feature.mapping"
                    :title="feature.title"
                    :disabled="$props.disabled"
                    @input.passive="update"
                  >
                    <option
                      v-for="option in feature.options"
                      :key="option.value"
                      :value="option.value"
                      v-html="option.label"
                      :selected="values[feature.mapping] === option.value"
                    />
                  </select>
                </template>
                <button
                  type="button"
                  class="btn btn-xs p-0 m-0 btn-rule-reset"
                  :title="`Remove ${feature.label} Rule`"
                  v-if="inputClasses[feature.mapping].ruleAdd"
                  @click="removeRule(feature.mapping)"
                  :disabled="$props.disabled"
                >
                  <i class="jam jam-close-circle-f" />
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
    FOR_HOLIDAY,
    FOR_SALE,
    FOR_RENT,
    RuleScopeProperty
} from '../consts';

import {
    corePropertyFeatures,
    corePropertyFeatureAsClasses,
    corePropertyFeatureAsValues
} from '../common';

import { makeAttributeRule, RuleKeyUpdate } from '../rules/attribute';

import { validateAttribute } from '../validation/attribute';

export default {
    data: () => ( {
        ui: {
            show: true
        },
        error: {
            show: false,
            msg: '',
            type: 'warning'
        },
        values: corePropertyFeatureAsValues(),
        inputClasses: corePropertyFeatureAsClasses()
    } ),
    computed: {
        listingTypes() {
            return this.$store.getters.listingTypes;
        },
        propertyTypes() {
            return this.$store.getters.propertyTypes;
        }
    },
    methods: {
        validate() {
            let state = {
                valid: true,
                msg: ''
            };

            return state;
        },
        update( event ) {
            this.error.show = false;
            const type = event.target.type;

            const attributeName = event.target.name;
            this.inputClasses[ attributeName ].hasError = false;

            let attributeValue = null;

            switch ( type ) {
            case 'text':
                attributeValue = event.target.value;

                if ( attributeValue === null || typeof attributeValue === 'undefined' ) {
                    attributeValue = '';
                    validate = false;
                }
                break;
            case 'number':
                attributeValue = event.target.valueAsNumber;

                if ( !attributeValue && attributeValue !== 0 ) {
                    attributeValue = null;
                    validate = false;
                }
                break;
            case 'select-one':
                attributeValue = event.target.value;
                break;
            }

            let validate = true;

            if ( validate ) {
                const validation = validateAttribute( attributeName, attributeValue );

                if ( validation.error !== null ) {
                    this.inputClasses[ attributeName ].hasError = true;
                    this.error.msg = validation.error.toString();
                    this.error.type = 'danger';
                    this.error.show = true;
                    this.values[ attributeName ] = this.values[ attributeName ];
                    return;
                }
            }

            this.values[ attributeName ] = attributeValue;

            const result = makeAttributeRule( {
                name: attributeName,
                value: attributeValue,
                key: RuleKeyUpdate,
                createdBy: this.$store.getters.email,
                scope: RuleScopeProperty
            } );

            if ( result.error !== null ) {
                this.error.msg = result.error.toString();
                this.error.show = true;
                return;
            }

            this.addRule( result.rule );
        },
        updatePropertyType( event ) {
            const attributeName = event.target.name;

            // format = "{typeID}:{groupID}"
            const attributeValue = event.target.options[ event.target.selectedIndex ].value;

            const result = makeAttributeRule( {
                name: attributeName,
                value: attributeValue,
                key: RuleKeyUpdate,
                createdBy: this.$store.getters.email,
                scope: RuleScopeProperty
            } );

            if ( result.error !== null ) {
                this.error.msg = result.error.toString();
                this.error.show = true;
                return;
            }

            this.values[ attributeName ] = attributeValue;

            this.addRule( result.rule );
        },
        updateOfferType( event ) {
            const attributeName = event.target.name;
            const attributeValue = event.target.options[ event.target.selectedIndex ].value;

            const result = makeAttributeRule( {
                name: attributeName,
                value: attributeValue,
                key: RuleKeyUpdate,
                createdBy: this.$store.getters.email,
                scope: RuleScopeProperty
            } );

            if ( result.error !== null ) {
                this.error.msg = result.error.toString();
                this.error.show = true;
                return;
            }

            this.values[ attributeName ] = attributeValue;

            this.addRule( result.rule );
        },
        addRule( rule ) {
            let rules = this.value.filter( r => r.name !== rule.name );
            rules.push( rule );
            this.$emit( 'input', rules );
            this.inputClasses[ rule.name ].ruleAdd = true;
            this.inputClasses[ rule.name ].ruleNone = false;
        },
        removeRule( ruleName ) {
            this.values[ ruleName ] = this.$props.property[ ruleName ];
            const rules = this.value.filter( r => r.name !== ruleName );
            this.$emit( 'input', rules );
            this.inputClasses[ ruleName ].ruleAdd = false;
            this.inputClasses[ ruleName ].hasError = false;
            this.inputClasses[ ruleName ].ruleNone = true;
            this.error.show = false;

            if ( ruleName === 'OfferType' ) {
                this.values[ ruleName ] = this.$props.property[ ruleName ].Name;
            }
        },
        reset() {
            this.ui = this.initialState;
            this.setState();
        },
        removeAllRules() {
            this.$emit( 'input', [] );

            corePropertyFeatures.forEach( attr => {
                this.inputClasses[ attr.mapping ] = {
                    ruleNone: true,
                    ruleAdd: false
                };
            } );

            this.inputClasses[ 'OfferType' ] = {
                ruleNone: true,
                ruleAdd: false
            };

            this.inputClasses[ 'PropertyType' ] = {
                ruleNone: true,
                ruleAdd: false
            };

            for ( const attr in this.values ) {
                this.values[ attr ] = this.$props.property[ attr ];
            }

            this.values[ 'OfferType' ] = this.$props.property.OfferType.Name;
            this.values[ 'PropertyType' ] = `${this.$props.property.Type.Id}:${this.$props.property.Type.GroupId}`;
        },
        setState() {
            corePropertyFeatures.forEach( attr => {
                this.inputClasses[ attr.mapping ] = {
                    ruleNone: true,
                    ruleAdd: false,
                    hasError: false
                };
            } );

            this.inputClasses[ 'OfferType' ] = {
                ruleNone: true,
                ruleAdd: false
            };

            this.inputClasses[ 'PropertyType' ] = {
                ruleNone: true,
                ruleAdd: false
            };

            for ( const attr in this.values ) {
                this.values[ attr ] = this.$props.property[ attr ];
            }

            this.values[ 'OfferType' ] = this.$props.property.OfferType.Name;
            this.values[ 'PropertyType' ] = `${this.$props.property.Type.Id}:${this.$props.property.Type.GroupId}`;

            this.$props.value.forEach( rule => {
                this.values[ rule.name ] = rule.value;
                this.inputClasses[ rule.name ].ruleAdd = true;
            } );
        }
    },
    created() {
        this.FOR_SALE = FOR_SALE;
        this.FOR_RENT = FOR_RENT;
        this.FOR_HOLIDAY = FOR_HOLIDAY;
        this.corePropertyFeatures = corePropertyFeatures;
        this.initialState = this.ui;
        this.setState();
    },
    props: {
        property: {
            required: false,
            type: Object,
            default: () => ( {
                Photos: []
            } )
        },
        value: {
            required: false,
            type: Array,
            default: () => []
        },
        disabled: {
            required: true,
            type: Boolean,
            default: () => false
        }
    }
};
</script>
<style scoped>
.has-error {
  border-color: #dc3545;
}
</style>



// WEBPACK FOOTER //
// src/components/property-qc/handlers/AttributeRuleHandler.vue